import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { usePlan } from '../context/PlanContext';

import {
  CircleNotch,
  Robot,
  Sparkle,
  X
} from '@phosphor-icons/react';
const CreateAgentFromRoleModal = ({ roleData, onClose, onSuccess, organizationId }) => {
  const { planLimits } = usePlan();
  const [agentName, setAgentName] = useState(roleData?.name ? `${roleData.name} Agent` : '');
  const [description, setDescription] = useState(roleData?.ai_opportunity || '');
  const [model, setModel] = useState('gpt-4o');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check plan limits before creating agent
    if (planLimits?.max_agents === 0) {
      setError('Your current plan does not allow creating agents. Please upgrade to Pro or Partner Pro.');
      return;
    }
    
    setIsCreating(true);
    setError('');

    try {
      console.log('[CreateAgentFromRoleModal] Creating agent with organizationId:', organizationId);
      
      // Build URL with organization_id as query param
      const url = new URL(`${BACKEND_URL}/api/agent/create`);
      if (organizationId) {
        url.searchParams.append('organization_id', organizationId);
      }
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: agentName,
          description: description,
          role: roleData?.name || 'AI Assistant',
          llm_provider: 'openai',
          llm_model: model || 'gpt-4',
          llm_temperature: 0.7,
          llm_max_tokens: 2000,
          system_prompt_override: `You are an AI agent for the ${roleData?.name} role in the ${roleData?.department || 'organization'}. ${description}`,
          api_key_source: 'user',
          capabilities: ['chat', 'analysis'],
          linked_task_descriptions: [roleData?.ai_opportunity || ''].filter(Boolean)
        })
      });
      
      console.log('[CreateAgentFromRoleModal] Response status:', response.status);

      const data = await response.json();

      if (response.ok) {
        onSuccess(data);
        onClose();
      } else {
        setError(data.detail || 'Failed to create agent');
      }
    } catch (err) {
      console.error('Error creating agent:', err);
      setError('Failed to create agent. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  console.log('[CreateAgentFromRoleModal] Rendering with roleData:', roleData);
  
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={(e) => {
      if (e.target === e.currentTarget) {
        console.log('[CreateAgentFromRoleModal] Backdrop clicked, closing modal');
        onClose();
      }
    }}>
      <div className="glass rounded-2xl border border-[rgba(255,255,255,0.1)] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
              <Robot size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Create AI Agent</h2>
              <p className="text-sm text-[#A0AEC0]">For: {roleData?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>
        </div>

        {/* Role Context */}
        <div className="glass rounded-xl p-4 mb-6 border border-[#6366F1]/30">
          <div className="flex items-start gap-3">
            <Sparkle size={20} className="text-[#F59E0B] flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">AI Opportunity Detected</h3>
              <p className="text-sm text-[#A0AEC0]">{roleData?.ai_opportunity}</p>
              {roleData?.department && (
                <p className="text-xs text-[#718096] mt-2">Department: {roleData.department}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Agent Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Agent Name *
            </label>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#5C6778] focus:outline-none focus:border-[#6366F1] transition-colors"
              placeholder="e.g., Marketing Strategy Agent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description / Purpose
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#5C6778] focus:outline-none focus:border-[#6366F1] transition-colors resize-none"
              placeholder="What will this agent do?"
            />
          </div>

          {/* AI Model */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              AI Model
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:border-[#6366F1] transition-colors"
            >
              <option value="gpt-4o">GPT-4o (Recommended)</option>
              <option value="gpt-4o-mini">GPT-4o Mini (Faster)</option>
              <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
              <option value="gemini-2.0-flash-exp">Gemini 2.0 Flash</option>
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-lg">
              <p className="text-sm text-[#EF4444]">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !agentName}
              className="flex-1 px-6 py-3 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isCreating ? (
                <>
                  <CircleNotch size={20} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Robot size={20} />
                  Create Agent
                </>
              )}
            </button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-6 p-4 bg-[#6366F1]/10 rounded-lg border border-[#6366F1]/30">
          <p className="text-xs text-[#A0AEC0]">
            💡 <span className="text-white font-medium">Note:</span> This agent will be linked to the &quot;{roleData?.name}&quot; role. 
            You can configure additional settings, tools, and knowledge sources after creation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateAgentFromRoleModal;
